import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonButton, IonButtons, IonIcon, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import { addIcons } from 'ionicons';
import { chevronBackSharp, chevronForwardSharp } from 'ionicons/icons';
import { CrosswordBoardConfig } from '../../crossword-module/crossword-board/crossword-board.component';
import { PuzzleTypeIdEnum } from '../../puzzle-framework-module/puzzle-type-id.enum';
import { CrosswordWordModel } from '../../crossword-module/models/crossword-word.model';
import { PuzzleFrameworkEvents } from '../../puzzle-framework-module/puzzle-framework-events.enum';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { DefinitionsListService } from '../../crossword-module/crossword-sidebar/definitions-list.service';

@Component({
  selector: 'lib-app-definition-bar',
  templateUrl: './definition-bar.component.html',
  styleUrls: ['./definition-bar.component.scss'],
  standalone: true,
  imports: [CommonModule, IonButton, IonButtons, IonIcon, IonTitle, IonToolbar],
})
export class DefinitionBarComponent implements OnInit, OnDestroy {
  showDefinitionBar = false;
  private definitionBarSubscription: Subscription;
  @Input() crosswordBoardConfig: CrosswordBoardConfig = {};

  private puzzleEventsSubscription: Subscription;

  finalCrosswordBoardConfig: CrosswordBoardConfig;

  currentWord: CrosswordWordModel = null;

  constructor(
    public store: Store,
    public framework: PuzzleFrameworkService,
    private definitionsListService: DefinitionsListService
  ) {
    addIcons({
      chevronBackSharp,
      chevronForwardSharp,
    });
  }

  public ngOnInit() {
    this.finalCrosswordBoardConfig = this.getFinalConfig(this.crosswordBoardConfig);

    this.definitionBarSubscription = this.store
      .select((state) => state.puzzleGameState.definitionBarEnabled)
      .subscribe((enabled) => {
        const shouldEnable =
          this.finalCrosswordBoardConfig.definitionBar === 'show' ||
          (this.finalCrosswordBoardConfig.definitionBar === 'userSelect' && enabled);
        if (this.showDefinitionBar !== shouldEnable) {
          this.showDefinitionBar = shouldEnable;
          // TODO This was copied from crossword-board.component, don't know if we still need it.
          // this.detectChanges();
          // this.scaleBottomWord();
        }
      });

    this.definitionsListService.currentWord$.subscribe((word) => {
      if (word === null || word === this.currentWord) {
        return;
      }
      this.currentWord = word;
    });
  }

  public ngOnDestroy() {
    this.definitionBarSubscription.unsubscribe();
  }

  onDefinitionBarPrevClicked($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    this.framework.puzzleEvents.emit(PuzzleFrameworkEvents.PREVIOUS_QUESTION);
  }
  onDefinitionBarNextClicked($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    this.framework.puzzleEvents.emit(PuzzleFrameworkEvents.NEXT_QUESTION);
  }

  // TODO this is a copy from crossword-board.component.ts should be in some service
  private getFinalConfig(crosswordBoardConfig: CrosswordBoardConfig) {
    let {
      crosswordSidebar,
      definFields,
      splitDefinList,
      splitWordList,
      fieldNumbers,
      definitionBar,
      skipPredefined,
      constantAutoZoomDimensions,
      showWordLengths,
    } = crosswordBoardConfig;

    const { configPreset } = crosswordBoardConfig;

    let presetValues: CrosswordBoardConfig = {};

    switch (configPreset) {
      case PuzzleTypeIdEnum.CROSSWORD_770:
      case PuzzleTypeIdEnum.CROSSWORD_771:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'hide',
          definFields: 'hide',
          splitDefinList: 'no',
          splitWordList: 'no',
          fieldNumbers: 'hide',
          skipPredefined: 'yes',
          constantAutoZoomDimensions: 'width',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_221:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'wordList',
          definFields: 'block',
          splitDefinList: 'no',
          splitWordList: 'length&orientation',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_20:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'hide',
          definFields: 'show',
          splitDefinList: 'no',
          splitWordList: 'no',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_310:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'definList',
          definFields: 'hide',
          splitDefinList: 'orientation',
          splitWordList: 'no',
          fieldNumbers: 'show',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_265:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'definList',
          definFields: 'hide',
          splitDefinList: 'no',
          splitWordList: 'no',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
      case PuzzleTypeIdEnum.CROSSWORD_252:
        presetValues = {
          definitionBar: 'hide',
          crosswordSidebar: 'wordList',
          definFields: 'hide',
          splitDefinList: 'no',
          splitWordList: 'length',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'fromPuzzleFile',
        };
        break;
      default:
        // Swedish
        presetValues = {
          definitionBar: 'userSelect',
          crosswordSidebar: 'hide',
          definFields: 'show',
          splitDefinList: 'no',
          splitWordList: 'no',
          fieldNumbers: 'hide',
          skipPredefined: 'no',
          constantAutoZoomDimensions: 'no',
          showWordLengths: 'no',
        };
        break;
    }

    crosswordSidebar =
      crosswordSidebar && crosswordSidebar !== 'default' ? crosswordSidebar : presetValues.crosswordSidebar;
    definFields = definFields && definFields !== 'default' ? definFields : presetValues.definFields;
    splitDefinList = splitDefinList && splitDefinList !== 'default' ? splitDefinList : presetValues.splitDefinList;
    splitWordList = splitWordList && splitWordList !== 'default' ? splitWordList : presetValues.splitWordList;
    fieldNumbers = fieldNumbers && fieldNumbers !== 'default' ? fieldNumbers : presetValues.fieldNumbers;
    definitionBar = definitionBar && definitionBar !== 'default' ? definitionBar : presetValues.definitionBar;
    skipPredefined = skipPredefined && skipPredefined !== 'default' ? skipPredefined : presetValues.skipPredefined;
    constantAutoZoomDimensions =
      constantAutoZoomDimensions && constantAutoZoomDimensions !== 'default'
        ? constantAutoZoomDimensions
        : presetValues.constantAutoZoomDimensions;
    showWordLengths = showWordLengths && showWordLengths !== 'default' ? showWordLengths : presetValues.showWordLengths;

    return {
      configPreset,
      crosswordSidebar,
      definFields,
      splitDefinList,
      splitWordList,
      fieldNumbers,
      definitionBar,
      skipPredefined,
      constantAutoZoomDimensions,
      showWordLengths,
    };
  }
}
